/**
 * domain.ts — Step 3: Working domain Ω (CMY subtractive model)
 *
 * buildWorkingDomain — mark patch.flags.outOfDomain based on CMY ink limits.
 * isOutOfDomain      — pure predicate for a single patch (exported for tests/UI).
 */

import type { MeasuredPatch, WorkingDomain } from '@/types';
import { DEFAULT_WORKING_DOMAIN } from '@/types';

// ─────────────────────────────────────────────────────────────────────────────
// Re-export the default so callers only need one import
// ─────────────────────────────────────────────────────────────────────────────
export { DEFAULT_WORKING_DOMAIN };

// ─────────────────────────────────────────────────────────────────────────────
// Pure predicate
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns true when the patch violates any CMY ink limit.
 *
 * A patch is OUTSIDE domain Ω when ANY of:
 *   C > cMax                              (primary C limit)
 *   M > mMax                              (primary M limit)
 *   Y > yMax                              (primary Y limit)
 *   C > cmMax  AND M > cmMax              (both channels deep on CM axis)
 *   C > cyMax  AND Y > cyMax              (both channels deep on CY axis)
 *   M > myMax  AND Y > myMax              (both channels deep on MY axis)
 *   C > cmyMax AND M > cmyMax AND Y > cmyMax  (all channels deep on CMY axis)
 *
 * The AND logic for overprint limits ensures they only fire when both/all channels
 * of that overprint axis are simultaneously loaded. Single-channel extremes are
 * caught by the primary limits (cMax / mMax / yMax).
 *
 * All limits ∈ [0,1]. Invariant: limits = 1.0 → no patch excluded (C,M,Y ≤ 1 always).
 */
export function isOutOfDomain(p: MeasuredPatch, d: WorkingDomain): boolean {
  const [C, M, Y] = p.cmy;
  return (
    C > d.cMax ||
    M > d.mMax ||
    Y > d.yMax ||
    (C > d.cmMax  && M > d.cmMax)  ||
    (C > d.cyMax  && Y > d.cyMax)  ||
    (M > d.myMax  && Y > d.myMax)  ||
    (C > d.cmyMax && M > d.cmyMax && Y > d.cmyMax)
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main: buildWorkingDomain
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Marks patch.flags.outOfDomain in-place for all patches that violate
 * any CMY limit in `domain`.
 *
 * Returns the same patch array (mutated).
 *
 * Invariant: with all limits = 1.0 (DEFAULT_WORKING_DOMAIN), no patch is excluded.
 * Channel values ≤ 1 → averages ≤ 1, so all axis-param checks pass.
 *
 * IMPORTANT: this is the ONLY function that may set outOfDomain.
 * diagnostics.ts and suggestDomain must NOT touch this flag.
 */
export function buildWorkingDomain(
  patches: MeasuredPatch[],
  domain:  WorkingDomain = DEFAULT_WORKING_DOMAIN,
): MeasuredPatch[] {
  for (const p of patches) {
    p.flags.outOfDomain = isOutOfDomain(p, domain);
  }
  return patches;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** RGB minimum equivalent of a CMY ink limit. */
export function rgbMinFromInkMax(inkMax: number): number {
  return Math.round((1 - inkMax) * 255);
}

/** Collect IDs of all excluded patches. */
export function excludedIds(patches: MeasuredPatch[]): string[] {
  return patches.filter(p => p.flags.outOfDomain).map(p => p.id);
}
